// Restaurant Guests report — every unique restaurant/bar customer with how many
// times they ordered, what they spent in total, and their last visit. Walk-in
// customers come from the captured order customer; room guests from the booking.
import React, { useEffect, useMemo, useState } from 'react';
import config from '../../config';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  TextField,
  Button,
  Chip,
} from '@mui/material';

const fmtDate = (v) => {
  if (!v) return '-';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '-' : d.toLocaleDateString('en-IN');
};

export default function RestaurantGuestReport() {
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const company = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');
  const currency = company?.currency ?? '₹';
  const token = sessionStorage.getItem('token');

  useEffect(() => {
    fetch(
      `${config.API_BASE_URL}/restaurantreport/guests/${encodeURIComponent(company?.id)}`,
      { headers: { Authorization: `Bearer ${token}` } },
    )
      .then((res) => res.json())
      .then((data) => setGuests(Array.isArray(data) ? data : []))
      .catch(() => setGuests([]))
      .finally(() => setLoading(false));
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return guests;
    return guests.filter(
      (g) =>
        (g.name || '').toLowerCase().includes(q) ||
        (g.mobile || '').includes(q),
    );
  }, [guests, search]);

  const exportCsv = () => {
    const rows = [
      ['Name', 'Mobile', 'Visits', 'Total Spent', 'Last Visit'],
      ...filtered.map((g) => [
        g.name || '-',
        g.mobile || '-',
        g.visits,
        Number(g.totalSpent || 0).toFixed(2),
        fmtDate(g.lastVisit),
      ]),
    ];
    const csv = rows
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'restaurant-guests.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <Box
        display='flex'
        justifyContent='center'
        alignItems='center'
        minHeight='60vh'>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      <Typography variant='h4' fontWeight='bold' mb={3}>
        🧑‍🍳 Restaurant Guests
      </Typography>

      <Box
        sx={{
          display: 'flex',
          gap: 1.5,
          flexWrap: 'wrap',
          alignItems: 'center',
          mb: 2,
        }}>
        <TextField
          size='small'
          label='Search name / mobile'
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 260 }}
        />
        <Chip label={`${filtered.length} guest(s)`} color='primary' />
        <Box flex={1} />
        <Button variant='contained' onClick={exportCsv}>
          Export CSV
        </Button>
      </Box>

      <Paper elevation={3}>
        <TableContainer sx={{ maxHeight: '70vh' }}>
          <Table stickyHeader size='small'>
            <TableHead>
              <TableRow>
                {['#', 'Name', 'Mobile', 'Visits', 'Total Spent', 'Last Visit'].map(
                  (h) => (
                    <TableCell key={h} sx={{ fontWeight: 'bold' }}>
                      {h}
                    </TableCell>
                  ),
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((g, i) => (
                <TableRow key={`${g.mobile}-${g.name}-${i}`} hover>
                  <TableCell>{i + 1}</TableCell>
                  <TableCell>{g.name || '-'}</TableCell>
                  <TableCell>{g.mobile || '-'}</TableCell>
                  <TableCell>
                    <Chip size='small' label={g.visits} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>
                    {currency}
                    {Number(g.totalSpent || 0).toFixed(2)}
                  </TableCell>
                  <TableCell>{fmtDate(g.lastVisit)}</TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align='center'>
                    No guests found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
